import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { CHAT_MESSAGE_MAX_CHARS, buildMessageTooLongError } from '@/lib/chat-limits'
import { useSettings } from '@/lib/settings'
import {
  asRecord,
  buildConversationTreeRows,
} from '@/pages/chat/chat-utils'
import type {
  Citation,
  ContextMode,
  ConversationTreeRow,
  Message,
  MiniappConversation,
  SourceItem,
} from '@/pages/chat/chat-types'
import {
  PROCESSING_STATUSES,
  normalizeSelectedDocIds,
  type UiDocumentItem,
} from '@/lib/documents-ui'

const LAST_CONV_KEY = 'miniapp_last_conversation_id'
const LAST_SPACE_KEY = 'miniapp_last_space_id'
const LAST_CONTEXT_MODE_KEY = 'miniapp_last_context_mode'
const SELECTED_DOCS_KEY = 'miniapp_selected_doc_ids'
const HOME_SELECTED_DOCS_KEY = 'miniapp_home_selected_doc_ids'
const HOME_PENDING_QUERY_KEY = 'miniapp_home_pending_query'
export const CHAT_INPUT_MIN_HEIGHT = 42
export const CHAT_INPUT_MAX_HEIGHT = 360

export function useChatPageController() {
  const queryClient = useQueryClient()
  const { settings, setTheme, setWebSearchMode } = useSettings()
  const [searchParams, setSearchParams] = useSearchParams()
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [conversationId, setConversationId] = useState<string | undefined>(() => {
    try { return localStorage.getItem(LAST_CONV_KEY) || undefined } catch { return undefined }
  })
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | undefined>(() => {
    try { return localStorage.getItem(LAST_SPACE_KEY) || undefined } catch { return undefined }
  })
  const [contextMode, setContextMode] = useState<ContextMode>(() => {
    try {
      const raw = localStorage.getItem(LAST_CONTEXT_MODE_KEY)
      return raw === 'space_shared' ? 'space_shared' : 'isolated'
    } catch {
      return 'isolated'
    }
  })
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(SELECTED_DOCS_KEY)
      if (!raw) return []
      return normalizeSelectedDocIds(JSON.parse(raw))
    } catch {
      return []
    }
  })
  const [remaining, setRemaining] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showConvList, setShowConvList] = useState(false)
  const [collapsedBranchIds, setCollapsedBranchIds] = useState<Set<string>>(new Set())
  const [showDocsPicker, setShowDocsPicker] = useState(false)
  const [documents, setDocuments] = useState<UiDocumentItem[]>([])
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsUploading, setDocsUploading] = useState(false)
  const [chatUploadJobId, setChatUploadJobId] = useState<string | null>(null)
  const [chatUploadInfo, setChatUploadInfo] = useState<string | null>(null)
  const [acceptAttr, setAcceptAttr] = useState('.pdf,.docx,.txt')
  const [maxUploadMb, setMaxUploadMb] = useState(25)
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(null)
  const chatMessagesRef = useRef<HTMLDivElement>(null)
  const chatInputRef = useRef<HTMLTextAreaElement>(null)
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const selectedDocsSyncRef = useRef<string>('')
  const selectedDocsHydratedConversationRef = useRef<string | null>(conversationId || null)
  // Изоляция стрима: при переключении беседы/сбросе поколение растёт, и «осиротевший» стрим
  // перестаёт трогать UI (в т.ч. НЕ дёргает setConversationId на complete, иначе утаскивает обратно
  // в стримящийся чат и «затекает» в открытые диалоги). streamingConvIdRef — беседа активного стрима.
  const sendGenRef = useRef(0)
  const streamingConvIdRef = useRef<string | null>(null)

  const { data: plan } = useQuery({
    queryKey: ['user-plan'],
    queryFn: () => apiClient.getUserPlan(),
    staleTime: 5 * 60 * 1000,
  })
  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => apiClient.getUserProfile(),
    staleTime: 60 * 1000,
  })

  const { data: spaces, refetch: refetchSpaces } = useQuery({
    queryKey: ['spaces'],
    queryFn: () => apiClient.getSpaces(200),
    staleTime: 60 * 1000,
  })

  const { data: conversations, refetch: refetchConversations, isFetching: conversationsFetching } = useQuery({
    queryKey: ['conversations', '__all__'],
    queryFn: () => apiClient.getConversations(100),
    staleTime: 30 * 1000,
  })

  const hasProcessingDocs = useMemo(
    () => documents.some((doc) => PROCESSING_STATUSES.has(String(doc.status || '').toLowerCase())),
    [documents],
  )
  const conversationTreeRows = useMemo<Array<ConversationTreeRow<MiniappConversation>>>(
    () => {
      const items = Array.isArray(conversations?.items) ? conversations.items : []
      return buildConversationTreeRows(items, collapsedBranchIds)
    },
    [conversations?.items, collapsedBranchIds],
  )

  useEffect(() => {
    const ids = new Set((conversations?.items || []).map((item) => item.id))
    setCollapsedBranchIds((prev) => {
      const next = new Set<string>()
      for (const id of prev) {
        if (ids.has(id)) next.add(id)
      }
      return next
    })
  }, [conversations?.items])

  const toggleBranchCollapse = useCallback((conversationId: string) => {
    setCollapsedBranchIds((prev) => {
      const next = new Set(prev)
      if (next.has(conversationId)) {
        next.delete(conversationId)
      } else {
        next.add(conversationId)
      }
      return next
    })
  }, [])

  const getTreePrefix = useCallback((row: ConversationTreeRow<MiniappConversation>): string => {
    if (row.depth <= 0) return ''
    const segments = row.ancestorHasNext.map((hasNext) => (hasNext ? '│ ' : '  ')).join('')
    return `${segments}${row.isLastSibling ? '└─' : '├─'}`
  }, [])

  const loadDocuments = useCallback(async (conversationIdOverride?: string) => {
    setDocsLoading(true)
    try {
      const effectiveConversationId = (conversationIdOverride || conversationId || undefined)
      const response = await apiClient.getDocuments({
        page: 1,
        pageSize: 200,
        conversationId: effectiveConversationId,
      })
      const items = Array.isArray(response.items) ? response.items : []
      setDocuments(items)
      const readyIds = new Set(
        items
          .filter((item) => String(item.status || '').toLowerCase() === 'ready')
          .map((item) => String(item.id || '').trim())
          .filter(Boolean),
      )
      setSelectedDocIds((prev) => {
        const filtered = prev.filter((id) => readyIds.has(id))
        return filtered.length === prev.length && filtered.every((value, index) => value === prev[index])
          ? prev
          : filtered
      })
    } catch (err) {
      setError((prev) => prev || (err instanceof Error ? err.message : 'Ошибка загрузки документов'))
    } finally {
      setDocsLoading(false)
    }
  }, [conversationId])

  useEffect(() => {
    try {
      localStorage.setItem(SELECTED_DOCS_KEY, JSON.stringify(selectedDocIds))
    } catch {
      // ignore
    }
  }, [selectedDocIds])

  useEffect(() => {
    try {
      if (selectedSpaceId) {
        localStorage.setItem(LAST_SPACE_KEY, selectedSpaceId)
      } else {
        localStorage.removeItem(LAST_SPACE_KEY)
      }
    } catch {
      // ignore
    }
  }, [selectedSpaceId])

  useEffect(() => {
    try {
      localStorage.setItem(LAST_CONTEXT_MODE_KEY, contextMode)
    } catch {
      // ignore
    }
  }, [contextMode])

  useEffect(() => {
    const items = Array.isArray(spaces?.items) ? spaces.items : []
    if (!items.length) return
    if (selectedSpaceId && items.some((space) => space.id === selectedSpaceId)) return
    const personal = items.find((space) => Boolean(space.is_personal))
    const fallback = personal || items[0]
    if (fallback?.id) {
      setSelectedSpaceId(fallback.id)
    }
  }, [spaces, selectedSpaceId])

  useEffect(() => {
    const loadCapabilities = async () => {
      try {
        const capabilities = await apiClient.getDocumentsCapabilities()
        const item = capabilities?.item || {}
        if (Array.isArray(item.allowed_extensions) && item.allowed_extensions.length > 0) {
          setAcceptAttr(item.allowed_extensions.join(','))
        }
        if (typeof item.max_upload_mb === 'number') {
          setMaxUploadMb(item.max_upload_mb)
        }
      } catch {
        // fallback
      }
    }
    void loadCapabilities()
  }, [])

  useEffect(() => {
    void loadDocuments()
  }, [loadDocuments])

  useEffect(() => {
    if (!hasProcessingDocs && !chatUploadJobId) return
    const timer = window.setInterval(() => {
      void loadDocuments()
    }, 2500)
    return () => window.clearInterval(timer)
  }, [chatUploadJobId, hasProcessingDocs, loadDocuments])

  useEffect(() => {
    if (!chatUploadJobId) return
    const timer = window.setInterval(async () => {
      try {
        const job = await apiClient.getDocumentsJob(chatUploadJobId)
        const status = String(job?.item?.status || '').toLowerCase()
        const docsFromJob = Array.isArray(job?.item?.documents) ? job.item.documents : []
        const readyIds = docsFromJob
          .filter((doc) => String(doc?.status || '').toLowerCase() === 'ready')
          .map((doc) => String(doc?.id || '').trim())
          .filter(Boolean)
        if (readyIds.length > 0) {
          setSelectedDocIds((prev) => Array.from(new Set([...prev, ...readyIds])))
        }
        await loadDocuments()
        if (status === 'completed' || status === 'failed') {
          if (status === 'completed') {
            setChatUploadInfo('Документ готов и прикреплён к чату.')
          } else {
            const errorText = String(job?.item?.error_details?.message || '').trim()
            if (errorText) setError(errorText)
            setChatUploadInfo('Не удалось обработать один или несколько документов.')
          }
          setChatUploadJobId(null)
        }
      } catch {
        await loadDocuments()
      }
    }, 2000)
    return () => window.clearInterval(timer)
  }, [chatUploadJobId, loadDocuments])

  useEffect(() => {
    if (!conversationId) return
    if (selectedDocsHydratedConversationRef.current !== conversationId) return
    const normalized = JSON.stringify([...selectedDocIds].sort())
    const syncKey = `${conversationId}:${normalized}`
    if (selectedDocsSyncRef.current === syncKey) return
    selectedDocsSyncRef.current = syncKey
    void apiClient.updateConversation(conversationId, { selected_doc_ids: selectedDocIds }).catch((err) => {
      const message = err instanceof Error ? err.message : ''
      if (!message.includes('404')) return
      setConversationId(undefined)
      setMessages([])
      setSelectedDocIds([])
      selectedDocsHydratedConversationRef.current = null
      selectedDocsSyncRef.current = ''
      try { localStorage.removeItem(LAST_CONV_KEY) } catch { /* ignore */ }
      void refetchConversations()
    })
  }, [conversationId, selectedDocIds])

  useEffect(() => {
    if (!conversationId) {
      selectedDocsHydratedConversationRef.current = null
    }
  }, [conversationId])

  // Reattach to an in-progress agent run after navigating away and back.
  // The run keeps going + persists server-side; here we replay its live tail so the
  // answer appears (and finishes streaming) instead of being stuck on just the question.
  const reattachInFlightRef = useRef<string | null>(null)
  const reattachToRun = useCallback(async (convId: string) => {
    if (!convId || reattachInFlightRef.current === convId) return
    reattachInFlightRef.current = convId
    const assistantId = `assistant-reattach-${Date.now()}`
    let created = false
    let active = false
    let fullContent = ''
    let finalCitations: Citation[] = []
    let finalSources: SourceItem[] = []
    const ensure = () => {
      if (created) return
      created = true
      setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '', isStreaming: true }])
      setIsLoading(true)
    }
    try {
      for await (const event of apiClient.reattachRun(convId)) {
        const eventType = event.type || 'info'
        if (eventType === 'no_active_run') return
        if (eventType === 'run_started' || eventType === 'run_reattached') { active = true; ensure(); continue }
        if (eventType === 'final') {
          if (event.answer) { active = true; ensure(); fullContent = String(event.answer); setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: fullContent } : m)) }
        } else if (eventType === 'partial_answer') {
          const partial = String(event.answer || '').trim()
          if (partial) { active = true; ensure(); fullContent = partial; setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: fullContent } : m)) }
        } else if (eventType === 'token' || eventType === 'content') {
          active = true; ensure(); fullContent += event.content || event.token || ''
          setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: fullContent } : m))
        } else if (eventType === 'complete') {
          active = true; ensure()
          const result = event.result || event
          fullContent = result.answer || fullContent
          finalCitations = (result.citations as Citation[]) || []
          finalSources = Array.isArray(result.sources) ? (result.sources as SourceItem[]) : []
          if (event.remaining_requests !== undefined) setRemaining(event.remaining_requests)
        } else if (eventType === 'error') {
          setError(event.message || 'Ошибка')
        } else {
          const stepMsg = event.message || event.step || ''
          if (stepMsg && created) setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, thinkingStatus: String(stepMsg).slice(0, 100) } : m))
        }
      }
      if (active && created) {
        const safe = fullContent.trim() ? fullContent : 'Не удалось получить ответ. Повторите запрос.'
        setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: safe, citations: finalCitations, sources: finalSources, isStreaming: false } : m))
        refetchConversations()
      }
    } catch { /* reattach is best-effort */ }
    finally {
      if (active) setIsLoading(false)
      reattachInFlightRef.current = null
    }
  }, [refetchConversations])

  // Load conversation history on mount or switch
  const loadIdRef = useRef(0)
  useEffect(() => {
    if (!conversationId) return
    try { localStorage.setItem(LAST_CONV_KEY, conversationId) } catch { /* ignore */ }
    // Не перечитываем историю беседы, по которой прямо сейчас идёт живой стрим (иначе DB-история
    // затрёт стримящийся ответ). Любую другую беседу грузим, даже если где-то ещё идёт стрим.
    if (conversationId === streamingConvIdRef.current) return
    const loadId = ++loadIdRef.current
    apiClient.getConversationMessages(conversationId).then(res => {
      if (loadId !== loadIdRef.current) return // stale — conversationId changed
      const loaded: Message[] = res.messages.map((m) => {
        const meta = asRecord(m.metadata) || {}
        return {
          id: `db-${m.id}`,
          role: m.role,
          content: m.text,
          citations: Array.isArray(meta.citations) ? (meta.citations as Citation[]) : undefined,
          sources: Array.isArray(meta.sources) ? (meta.sources as SourceItem[]) : undefined,
        }
      })
      setMessages(loaded)
      // If history ends on a user message, an answer may still be generating
      // server-side (we navigated away mid-run) — reattach to its live tail.
      if (!isLoading && loaded.length > 0 && loaded[loaded.length - 1].role === 'user') {
        void reattachToRun(conversationId)
      }
    }).catch((err) => {
      if (loadId !== loadIdRef.current) return
      const message = err instanceof Error ? err.message : ''
      if (!message.includes('404')) return
      setConversationId(undefined)
      setMessages([])
      setSelectedDocIds([])
      selectedDocsHydratedConversationRef.current = null
      selectedDocsSyncRef.current = ''
      try { localStorage.removeItem(LAST_CONV_KEY) } catch { /* ignore */ }
      void refetchConversations()
    })
  }, [conversationId])

  useEffect(() => {
    if (!conversationId || !conversations?.items?.length) return
    const current = conversations.items.find((item) => item.id === conversationId)
    if (!current) {
      // НЕ сбрасываем только что созданную/стримящуюся беседу: её ещё нет в кэше списка (refetch не
      // завершился) — иначе новый чат у вернувшихся пользователей терял id и сообщение «висело» без
      // беседы. Сброс нужен лишь при реальном удалении на другом устройстве → ждём свежий список.
      if (conversationId === streamingConvIdRef.current || conversationsFetching) return
      setConversationId(undefined)
      setMessages([])
      setSelectedDocIds([])
      selectedDocsHydratedConversationRef.current = null
      selectedDocsSyncRef.current = ''
      try { localStorage.removeItem(LAST_CONV_KEY) } catch { /* ignore */ }
      return
    }
    if (current.space_id) {
      setSelectedSpaceId(current.space_id)
    }
    const mode = current.context_mode === 'space_shared' ? 'space_shared' : 'isolated'
    setContextMode(mode)
    if (!Array.isArray(current.selected_doc_ids)) {
      setSelectedDocIds([])
      selectedDocsHydratedConversationRef.current = conversationId
      return
    }
    const normalized = current.selected_doc_ids.map((item) => String(item || '').trim()).filter(Boolean)
    setSelectedDocIds(normalized)
    selectedDocsHydratedConversationRef.current = conversationId
  }, [conversationId, conversations, conversationsFetching])

  const scrollToBottom = useCallback(() => {
    const el = chatMessagesRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  useEffect(() => { scrollToBottom() }, [messages, scrollToBottom])

  const startNewConversation = async () => {
    sendGenRef.current++
    streamingConvIdRef.current = null
    setIsLoading(false)
    try {
      const res = await apiClient.createConversation(undefined, [], {
        spaceId: selectedSpaceId,
        contextMode,
      })
      setConversationId(res.id)
      if (res.space_id) setSelectedSpaceId(res.space_id)
      if (res.context_mode === 'isolated' || res.context_mode === 'space_shared') {
        setContextMode(res.context_mode)
      }
      setSelectedDocIds([])
      selectedDocsHydratedConversationRef.current = res.id
      selectedDocsSyncRef.current = ''
      setMessages([])
      setError(null)
      setShowConvList(false)
      setShowDocsPicker(false)
      setChatUploadJobId(null)
      setChatUploadInfo(null)
      refetchConversations()
    } catch { /* ignore */ }
  }

  const switchConversation = (
    convId: string,
    conversationDocIds?: string[] | null,
    options?: { spaceId?: string | null; contextMode?: ContextMode },
  ) => {
    // Прерываем возможный живой стрим: «осиротевший» стрим не трогает UI и не вернёт нас назад
    // через setConversationId(complete). Чистим сообщения сразу — load-эффект подтянет историю.
    sendGenRef.current++
    streamingConvIdRef.current = null
    setIsLoading(false)
    setMessages([])
    const normalized = Array.isArray(conversationDocIds)
      ? conversationDocIds.map((item) => String(item || '').trim()).filter(Boolean)
      : []
    setSelectedDocIds(normalized)
    setConversationId(convId)
    if (options?.spaceId) {
      setSelectedSpaceId(options.spaceId)
    }
    if (options?.contextMode === 'isolated' || options?.contextMode === 'space_shared') {
      setContextMode(options.contextMode)
    }
    selectedDocsHydratedConversationRef.current = convId
    setError(null)
    setShowConvList(false)
    setShowDocsPicker(false)
    setChatUploadInfo(null)
  }

  const ensureConversationForDocuments = useCallback(async () => {
    if (conversationId) return conversationId
    const created = await apiClient.createConversation(undefined, selectedDocIds, {
      spaceId: selectedSpaceId,
      contextMode,
    })
    setConversationId(created.id)
    if (created.space_id) setSelectedSpaceId(created.space_id)
    if (created.context_mode === 'isolated' || created.context_mode === 'space_shared') {
      setContextMode(created.context_mode)
    }
    selectedDocsHydratedConversationRef.current = created.id
    refetchConversations()
    return created.id
  }, [conversationId, refetchConversations, selectedDocIds, selectedSpaceId, contextMode])

  const toggleSelectedDocument = (doc: UiDocumentItem) => {
    const docId = String(doc.id || '').trim()
    if (!docId) return
    const ready = String(doc.status || '').toLowerCase() === 'ready'
    if (!ready) return
    setSelectedDocIds((prev) => (
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId]
    ))
    setChatUploadInfo(null)
  }

  const uploadDocumentsToChat = async (files: FileList | null) => {
    if (!files || files.length === 0 || docsUploading) return
    setDocsUploading(true)
    setError(null)
    setChatUploadInfo(null)
    try {
      const convId = await ensureConversationForDocuments()
      const result = await apiClient.uploadChatDocuments(Array.from(files), convId)
      const jobId = String(result?.item?.job_id || '').trim()
      if (jobId) {
        setChatUploadJobId(jobId)
        setChatUploadInfo('Документ загружен. Идёт индексация для чата…')
      }
      const readyFromResponse = (result?.item?.files || [])
        .filter((item) => String(item?.status || '').toLowerCase() === 'ready')
        .map((item) => String(item?.id || '').trim())
        .filter(Boolean)
      if (readyFromResponse.length > 0) {
        setSelectedDocIds((prev) => Array.from(new Set([...prev, ...readyFromResponse])))
      }
      await loadDocuments(convId)
      refetchConversations()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки документа в чат'
      setError(message)
    } finally {
      setDocsUploading(false)
      if (uploadInputRef.current) uploadInputRef.current.value = ''
    }
  }

  const applyContextMode = async (nextMode: ContextMode) => {
    if (nextMode === contextMode) return
    const previousMode = contextMode
    setContextMode(nextMode)
    if (!conversationId) return
    try {
      await apiClient.updateConversation(conversationId, { context_mode: nextMode })
      refetchConversations()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось переключить режим контекста'
      setError(message)
      setContextMode(previousMode)
    }
  }

  const applySpaceSelection = async (nextSpaceId: string) => {
    const trimmed = String(nextSpaceId || '').trim()
    if (!trimmed || trimmed === selectedSpaceId) return
    const previous = selectedSpaceId
    setSelectedSpaceId(trimmed)
    if (!conversationId) return
    try {
      await apiClient.updateConversation(conversationId, { space_id: trimmed })
      await refetchSpaces()
      await refetchConversations()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось изменить проект чата'
      setError(message)
      setSelectedSpaceId(previous)
    }
  }

  const renameConversation = async (conversationId: string, title: string) => {
    const convId = String(conversationId || '').trim()
    const nextTitle = String(title || '').trim()
    if (!convId || !nextTitle) return
    try {
      await apiClient.updateConversation(convId, { title: nextTitle })
      await refetchConversations()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось переименовать чат'
      setError(message)
    }
  }

  const refreshSpaces = async () => {
    await refetchSpaces()
  }

  const handleBranchConversation = async (
    conversation: { id: string; selected_doc_ids?: string[] | null; space_id?: string | null; context_mode?: ContextMode },
  ) => {
    try {
      const created = await apiClient.branchConversation(conversation.id)
      setConversationId(created.id)
      selectedDocsHydratedConversationRef.current = created.id
      if (created.space_id) setSelectedSpaceId(created.space_id)
      if (created.context_mode === 'isolated' || created.context_mode === 'space_shared') {
        setContextMode(created.context_mode)
      } else if (conversation.context_mode) {
        setContextMode(conversation.context_mode)
      }
      const normalized = Array.isArray(created.selected_doc_ids)
        ? created.selected_doc_ids.map((item) => String(item || '').trim()).filter(Boolean)
        : Array.isArray(conversation.selected_doc_ids)
          ? conversation.selected_doc_ids.map((item) => String(item || '').trim()).filter(Boolean)
          : []
      setSelectedDocIds(normalized)
      setMessages([])
      setShowConvList(false)
      await refetchConversations()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось создать ветку'
      setError(message)
    }
  }

  const handleDeleteConversation = async (conversation: MiniappConversation) => {
    if (!window.confirm('Удалить диалог?')) return
    try {
      await apiClient.deleteConversation(conversation.id)
      if (conversation.id === conversationId) {
        setConversationId(undefined)
        setSelectedDocIds([])
        selectedDocsHydratedConversationRef.current = null
        setMessages([])
      }
      await refetchConversations()
    } catch {
      // ignore
    }
  }

  const sendMessage = async (
    overrideText?: string,
    forceNewConversation?: boolean,
    options?: {
      spaceId?: string
      contextMode?: ContextMode
    },
  ) => {
    const text = (overrideText || input).trim()
    if (!text || isLoading) return
    if (text.length > CHAT_MESSAGE_MAX_CHARS) {
      setError(buildMessageTooLongError(CHAT_MESSAGE_MAX_CHARS))
      return
    }

    setInput('')
    setError(null)
    // Поколение этого стрима. Если во время «думания» переключить беседу/сбросить чат, sendGenRef
    // изменится — и все обновления UI ниже станут no-op (стрим «осиротеет»).
    const gen = ++sendGenRef.current
    const userMsg: Message = { id: `user-${Date.now()}`, role: 'user', content: text }
    setMessages(prev => [...prev, userMsg])

    const assistantId = `assistant-${Date.now()}`
    setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '', isStreaming: true }])
    setIsLoading(true)

    const convId = forceNewConversation ? undefined : conversationId
    // Беседа активного стрима: её историю load-эффект не перечитывает (не затрёт ответ).
    if (convId) streamingConvIdRef.current = convId
    const effectiveContextMode = options?.contextMode || contextMode
    let effectiveSpaceId = options?.spaceId !== undefined ? options.spaceId : selectedSpaceId
    if (!convId && effectiveSpaceId) {
      const knownSpaceIds = new Set(
        (Array.isArray(spaces?.items) ? spaces.items : [])
          .map((space) => String(space.id || '').trim())
          .filter(Boolean),
      )
      if (knownSpaceIds.size > 0 && !knownSpaceIds.has(effectiveSpaceId)) {
        effectiveSpaceId = undefined
      }
    }

    try {
      let fullContent = ''
      let finalCitations: Citation[] = []
      let finalSources: SourceItem[] = []

      for await (const event of apiClient.streamChat(
        text,
        convId,
        settings.selectedModel,
        selectedDocIds,
        {
          spaceId: effectiveSpaceId,
          contextMode: effectiveContextMode,
          webSearchMode: settings.webSearchMode,
        },
      )) {
        // Пользователь ушёл на другую беседу/сбросил чат во время стрима — прекращаем трогать UI
        // (в частности, НЕ дёргаем setConversationId на complete, чтобы не «утащить» обратно).
        if (sendGenRef.current !== gen) break
        const eventType = event.type || 'info'

        if (eventType === 'thinking' || eventType === 'step' || eventType === 'step_start') {
          const stepMsg = event.message || event.step || ''
          if (stepMsg) {
            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, thinkingStatus: stepMsg } : m))
          }
        } else if (eventType === 'thought') {
          const thought = event.message || ''
          if (thought) {
            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, thinkingStatus: `Размышляю: ${thought.slice(0, 80)}${thought.length > 80 ? '...' : ''}` } : m))
          }
        } else if (eventType === 'action') {
          const toolName = event.tool || ''
          const toolLabels: Record<string, string> = {
            mcp_search: 'Ищу в законах',
            mcp_get_article: 'Читаю статью',
            cite_law: 'Подготавливаю цитату',
            qdrant_search: 'Семантический поиск',
            document_analyze: 'Анализирую документ',
            web_search: 'Ищу в интернете',
          }
          setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, thinkingStatus: toolLabels[toolName] || `Инструмент: ${toolName}` } : m))
        } else if (eventType === 'observation') {
          const msg = event.message || ''
          if (msg) {
            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, thinkingStatus: msg.slice(0, 100) } : m))
          }
        } else if (eventType === 'final') {
          setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, thinkingStatus: 'Формирую ответ...' } : m))
          if (event.answer) {
            fullContent = event.answer
            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: fullContent } : m))
          }
        } else if (eventType === 'partial_answer') {
          const partial = String(event.answer || '').trim()
          if (partial) {
            fullContent = partial
            const issueIndex = Number((event.data as Record<string, unknown> | undefined)?.issue_index || 0)
            const issueTotal = Number((event.data as Record<string, unknown> | undefined)?.issue_total || 0)
            const status = issueIndex > 0 && issueTotal > 0
              ? `Готов блок ${issueIndex} из ${issueTotal}`
              : 'Готова часть составного ответа'
            setMessages(prev => prev.map(m =>
              m.id === assistantId
                ? { ...m, content: fullContent, thinkingStatus: status }
                : m
            ))
          }
        } else if (eventType === 'token' || eventType === 'content') {
          fullContent += event.content || event.token || ''
          setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: fullContent } : m))
        } else if (eventType === 'complete') {
          const result = event.result || event
          fullContent = result.answer || fullContent
          finalCitations = result.citations || []
          finalSources = Array.isArray(result.sources) ? (result.sources as SourceItem[]) : []
          if (event.conversation_id || result.conversation_id) {
            const newId = event.conversation_id || result.conversation_id
            streamingConvIdRef.current = newId
            setConversationId(newId)
            selectedDocsHydratedConversationRef.current = newId
          }
          if (event.remaining_requests !== undefined) setRemaining(event.remaining_requests)
        } else if (eventType === 'error') {
          setError(event.message || 'Ошибка')
        }
      }

      if (sendGenRef.current !== gen) return // стрим осиротел (пользователь переключил беседу)
      const safeContent = fullContent.trim() ? fullContent : 'Не удалось получить ответ. Повторите запрос.'
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: safeContent, citations: finalCitations, sources: finalSources, isStreaming: false }
          : m
      ))

      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
      refetchConversations()
    } catch (err) {
      if (sendGenRef.current !== gen) return
      const msg = err instanceof Error ? err.message : 'Ошибка соединения'
      const normalized = msg.includes('Failed to fetch') || msg.toLowerCase().includes('network')
        ? 'Ошибка сети. Проверьте подключение и попробуйте снова.'
        : msg
      setError(normalized.includes('429') ? 'Лимит бесплатных запросов исчерпан. Перейдите на Premium.' : normalized)
      setMessages(prev => prev.filter(m => m.id !== assistantId))
    } finally {
      if (sendGenRef.current === gen) {
        setIsLoading(false)
        streamingConvIdRef.current = null
      }
    }
  }

  const resizeChatInput = useCallback(() => {
    const el = chatInputRef.current
    if (!el) return
    el.style.height = '0px'
    const nextHeight = Math.min(Math.max(el.scrollHeight, CHAT_INPUT_MIN_HEIGHT), CHAT_INPUT_MAX_HEIGHT)
    el.style.height = `${nextHeight}px`
    el.style.overflowY = el.scrollHeight > CHAT_INPUT_MAX_HEIGHT ? 'auto' : 'hidden'
  }, [])

  useEffect(() => {
    resizeChatInput()
  }, [input, resizeChatInput])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() }
  }

  // Auto-send query from URL params (e.g., from home page quick input)
  const didAutoSendRef = useRef(false)

  useEffect(() => {
    if (didAutoSendRef.current) return
    const homeHandoff = searchParams.get('home_handoff') === '1'
    let q = String(searchParams.get('q') || '').trim()
    const isNew = searchParams.get('new') === '1'
    const keepDocs = searchParams.get('keep_docs') === '1'
    const docsSource = String(searchParams.get('docs_source') || '').trim().toLowerCase()
    const webMode = String(searchParams.get('web_mode') || '').trim().toLowerCase()
    if (!q && homeHandoff) {
      try {
        q = String(localStorage.getItem(HOME_PENDING_QUERY_KEY) || '').trim()
      } catch {
        q = ''
      }
    }
    if (homeHandoff) {
      try {
        localStorage.removeItem(HOME_PENDING_QUERY_KEY)
      } catch {
        // ignore
      }
    }
    if (webMode === 'off' || webMode === 'auto' || webMode === 'on') {
      setWebSearchMode(webMode)
    }
    if (!q) return
    didAutoSendRef.current = true
    setSearchParams({}, { replace: true })

    // Immediately reset state and bump loadIdRef to discard any in-flight loads
    if (isNew) {
      loadIdRef.current++
      setConversationId(undefined)
      setMessages([])
      setChatUploadInfo(null)
      setChatUploadJobId(null)
      const handoffFromHome = docsSource === 'home'
      if (handoffFromHome) {
        setSelectedSpaceId(undefined)
        setContextMode('isolated')
        try {
          localStorage.removeItem(LAST_SPACE_KEY)
          localStorage.setItem(LAST_CONTEXT_MODE_KEY, 'isolated')
        } catch {
          // ignore
        }
      }
      if (keepDocs && docsSource === 'home') {
        const homeDocIds = (() => {
          try {
            const raw = localStorage.getItem(HOME_SELECTED_DOCS_KEY)
            if (!raw) return []
            return normalizeSelectedDocIds(JSON.parse(raw))
          } catch {
            return []
          }
        })()
        setSelectedDocIds(homeDocIds)
        selectedDocsSyncRef.current = ''
        selectedDocsHydratedConversationRef.current = null
        try {
          localStorage.setItem(SELECTED_DOCS_KEY, JSON.stringify(homeDocIds))
          localStorage.removeItem(HOME_SELECTED_DOCS_KEY)
        } catch {
          // ignore
        }
      } else if (!keepDocs) {
        setSelectedDocIds([])
        selectedDocsSyncRef.current = ''
        selectedDocsHydratedConversationRef.current = null
        try { localStorage.setItem(SELECTED_DOCS_KEY, '[]') } catch { /* ignore */ }
      }
      try { localStorage.removeItem(LAST_CONV_KEY) } catch { /* ignore */ }
    }

    // Small delay to let React flush state updates, then send
    const timer = setTimeout(() => {
      const handoffFromHome = isNew && docsSource === 'home'
      sendMessage(q, isNew, {
        spaceId: handoffFromHome ? undefined : selectedSpaceId,
        contextMode: handoffFromHome ? 'isolated' : contextMode,
      })
    }, 50)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const panel = String(searchParams.get('panel') || '').trim().toLowerCase()
    if (!panel) return

    if (panel === 'history') {
      setShowDocsPicker(false)
      setShowConvList(true)
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('panel')
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  return {
    settings,
    setTheme,
    setWebSearchMode,
    plan,
    profile,
    spaces,
    conversations,
    messages,
    error,
    remaining,
    conversationId,
    selectedSpaceId,
    contextMode,
    selectedDocIds,
    showConvList,
    showDocsPicker,
    documents,
    docsLoading,
    docsUploading,
    chatUploadJobId,
    chatUploadInfo,
    acceptAttr,
    maxUploadMb,
    selectedCitation,
    chatMessagesRef,
    chatInputRef,
    uploadInputRef,
    input,
    isLoading,
    conversationTreeRows,
    setSelectedSpaceId,
    setShowConvList,
    setShowDocsPicker,
    setSelectedCitation,
    setInput,
    handleKeyDown,
    toggleBranchCollapse,
    getTreePrefix,
    startNewConversation,
    applySpaceSelection,
    refreshSpaces,
    renameConversation,
    switchConversation,
    handleBranchConversation,
    handleDeleteConversation,
    applyContextMode,
    loadDocuments,
    toggleSelectedDocument,
    uploadDocumentsToChat,
    sendMessage,
  }
}
